import { FC, Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Divider, Stack, Typography } from '@mui/material';
import { Navigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import { PrivateHat } from 'src/layout/private-hat';
import {
    FeedbackForm,
    FeedbackFormData,
    feedbackFormDefaultValues,
    FeedbackFormFail,
    FeedbackFormFields,
    FeedbackFormSuccess,
    FeedbackSchema,
    Subject,
    SUBJECT_TITLE_TRANSLATION_KEY,
    SUBJECT_VALUES,
    usePostFeedback,
} from 'src/features/feedback';
import { routes } from 'src/constants/routes';
import { EventType, GA4 } from 'src/lib/ga4';
import { LanguagesCode } from 'src/i18n/constants';
import { setAttachmentError } from 'src/components/attachment-gallery/utils/set-attachment-error';
import { SUBJECT_SUBCATEGORY_TRANSLATION_KEY } from 'src/features/feedback/constants/translation-keys';

export const FeedbackFormPage: FC = () => {
    const { t, i18n } = useTranslation();

    const { subject, subcategory } = useParams<{
        subject?: Subject;
        subcategory?: string;
    }>();

    const { control, handleSubmit, formState, setError, clearErrors } = useForm<FeedbackFormData>({
        resolver: yupResolver(FeedbackSchema),
        defaultValues: feedbackFormDefaultValues,
    });

    const { mutate, isSuccess, isLoading, isError, reset, data } = usePostFeedback({
        onError: () => setAttachmentError(FeedbackFormFields.Attachments, setError),
    });

    if (!subject || !Object.values(Subject).includes(subject) || !subcategory) {
        return <Navigate to={routes.feedback.index} />;
    }

    const englishTranslation = i18n.getFixedT(LanguagesCode.English);
    const subcategoryLabel = englishTranslation(
        SUBJECT_SUBCATEGORY_TRANSLATION_KEY[subject]?.[subcategory] ?? ''
    );

    const handleFormSubmit = handleSubmit((formData) => {
        const submitData = {
            [FeedbackFormFields.Rating]: formData[FeedbackFormFields.Rating]!,
            [FeedbackFormFields.Comment]: formData[FeedbackFormFields.Comment],
            [FeedbackFormFields.Attachments]: formData[FeedbackFormFields.Attachments],
            [FeedbackFormFields.Subject]: SUBJECT_VALUES[subject],
            [FeedbackFormFields.Subcategory]: subcategoryLabel,
        };

        mutate(submitData);

        GA4.event('Feedback Form Submit', EventType.Submit, {
            label: t('feedback_page_cta', { lng: LanguagesCode.English }),
            location: 'Feedback Form',
            data: submitData,
        });
    });

    if (isError && formState.isValid) {
        return (
            <FeedbackFormFail title={t(SUBJECT_TITLE_TRANSLATION_KEY[subject])} onRetry={reset} />
        );
    }

    if (isSuccess) {
        return <FeedbackFormSuccess subject={subject} reset={reset} feedback={data} />;
    }

    return (
        <Fragment>
            <PrivateHat
                title={t(SUBJECT_TITLE_TRANSLATION_KEY[subject])}
                backIconText={t('back_cta')}
            />

            <Stack
                padding={{ xs: '16px', md: '40px' }}
                justifyContent={{ md: 'center' }}
                alignItems="center"
                flexGrow={1}
            >
                <Stack maxWidth="500px" gap={{ xs: '40px', md: '48px' }}>
                    {subcategoryLabel && (
                        <Fragment>
                            <Typography
                                variant="overlineRegular"
                                color="text.primary"
                                textAlign={{ md: 'center' }}
                            >
                                <Trans
                                    i18nKey="feedback_page_subheader"
                                    components={{
                                        strong: (
                                            <Typography
                                                variant="overlineMedium"
                                                color="primary.main"
                                            />
                                        ),
                                    }}
                                    values={{
                                        subcategory: subcategoryLabel.toUpperCase(),
                                    }}
                                />
                            </Typography>
                            <Divider />
                        </Fragment>
                    )}

                    <FeedbackForm
                        onFormSubmit={handleFormSubmit}
                        formState={formState}
                        control={control}
                        isLoading={isLoading}
                        subject={subject}
                        setError={setError}
                        clearErrors={clearErrors}
                    />
                </Stack>
            </Stack>
        </Fragment>
    );
};
